import * as THREE from 'three';
import {
  CRAFT_BLOCK_TYPE,
  CRAFT_GRID_SIZE,
  blockTransformMatrix,
  mirrorBlockTransform,
  packVoxelMaterial,
  rotateBlockTransform,
  type BlockTransformAxis,
  CustomBlockDefinition,
  LibraryBlockDefinition,
  TEXTURE_GRID_SIZE,
  TexturePixels,
  textureGridSizeFromPixels,
  VoxelMaterial,
  voxelMaterialGlow,
} from '@shared/protocol';
import { World } from '../world/World';
import { VoxelHit } from '../world/raycast';
import {
  buildMicroVoxelGeometries,
  isEmptyGeometry,
  isVoxelOpaque,
  microVoxelIndex,
} from '../world/MicroVoxelGeometry';
import { showToast } from '../ui/toast';
import { createFaceOutlineGeometry, orientFaceOutline } from './FaceHighlight';

const REACH = 10;
const BREAK_SECONDS = 1;

export interface CraftPosition {
  x: number;
  y: number;
  z: number;
}

export type CraftFaceAction = 'save' | 'overwrite' | 'reset';

interface MicroHit extends VoxelHit {
  craft: CraftPosition;
}

interface PlacementTarget {
  craft: CraftPosition;
  x: number;
  y: number;
  z: number;
}

interface MicroAnchor extends PlacementTarget {
  type: VoxelMaterial;
}

interface TextureHit {
  craft: CraftPosition;
  face: number;
  index: number;
  nx: number;
  ny: number;
  nz: number;
  u: number;
  v: number;
  cx: number;
  cy: number;
  cz: number;
}

interface TextureDraft {
  color: string;
  pixels: TexturePixels;
  gridSize: number;
  source: Extract<LibraryBlockDefinition, { kind: 'textured' }> | null;
  viewTransform: number;
}

interface CraftingOptions {
  canvas: HTMLElement;
  save(
    name: string,
    gridSize: number,
    voxels: Array<[number, VoxelMaterial]>,
    overwriteId: number | null,
  ): Promise<CustomBlockDefinition>;
  saveTexture(
    name: string,
    color: string,
    pixels: TexturePixels,
    overwriteId: number | null,
  ): Promise<LibraryBlockDefinition>;
}

/** Client-side 16³ editor hosted in the one-block volume above a craft block. */
export class CraftingSystem {
  private readonly drafts = new Map<string, Map<number, VoxelMaterial>>();
  private readonly draftGridSizes = new Map<string, number>();
  private readonly draftSources = new Map<string, CustomBlockDefinition>();
  private readonly draftMeshes = new Map<string, THREE.Group>();
  private readonly textureDrafts = new Map<string, TextureDraft>();
  private readonly textureMeshes = new Map<string, THREE.Mesh>();
  private readonly opaqueMaterial = new THREE.MeshLambertMaterial({
    vertexColors: true,
  });
  private readonly transparentMaterial = new THREE.MeshLambertMaterial({
    vertexColors: true,
    transparent: true,
    depthWrite: false,
  });
  private readonly glowingMaterial = new THREE.MeshBasicMaterial({
    vertexColors: true,
    toneMapped: false,
  });
  private readonly direction = new THREE.Vector3();
  private readonly ray = new THREE.Ray();
  private readonly box = new THREE.Box3();
  private readonly point = new THREE.Vector3();
  private readonly boundary: THREE.LineSegments;
  private readonly voxelHighlight: THREE.LineSegments;
  private readonly voxelHighlightMaterial: THREE.LineBasicMaterial;
  private readonly massPreview: THREE.LineSegments;
  private readonly status = document.getElementById('craft-status')!;
  private readonly dialog = document.getElementById('craft-dialog')!;
  private readonly progress = document.getElementById('break-progress')!;
  private readonly progressFill = document.getElementById('break-progress-fill')!;

  private activeCraft: CraftPosition | null = null;
  private microHit: MicroHit | null = null;
  private placementTarget: PlacementTarget | null = null;
  private textureHit: TextureHit | null = null;
  private removeTarget: MicroHit | null = null;
  private removeElapsed = 0;
  private anchor: MicroAnchor | null = null;
  private anchorKind: 'place' | 'replace' | null = null;
  private massBuild = false;
  private selectedVoxelMaterial: VoxelMaterial | null = null;
  private textureModePreview = false;

  constructor(
    private readonly world: World,
    private readonly scene: THREE.Scene,
    private readonly options: CraftingOptions,
  ) {
    const unitEdges = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
    this.boundary = new THREE.LineSegments(
      unitEdges,
      new THREE.LineBasicMaterial({
        color: 0xffcb64,
        transparent: true,
        opacity: 0.75,
        depthTest: false,
      }),
    );
    this.boundary.renderOrder = 11;
    this.boundary.visible = false;
    this.voxelHighlightMaterial = new THREE.LineBasicMaterial({
      color: 0xffffff,
      depthTest: false,
    });
    this.voxelHighlight = new THREE.LineSegments(
      createFaceOutlineGeometry(),
      this.voxelHighlightMaterial,
    );
    this.voxelHighlight.renderOrder = 12;
    this.voxelHighlight.scale.setScalar(0.9 / CRAFT_GRID_SIZE);
    this.voxelHighlight.visible = false;
    this.massPreview = new THREE.LineSegments(
      unitEdges,
      new THREE.LineBasicMaterial({
        color: 0x35e6ff,
        depthTest: false,
      }),
    );
    this.massPreview.renderOrder = 13;
    this.massPreview.visible = false;
    scene.add(this.boundary, this.voxelHighlight, this.massPreview);

  }

  /** True when the micro editor owns the nearest actionable ray target. */
  get hasTargetHighlight(): boolean {
    return this.voxelHighlight.visible || this.boundary.visible;
  }

  get currentTextureFaceTarget(): VoxelHit | null {
    const hit = this.textureHit;
    if (!hit) return null;
    return {
      x: hit.craft.x,
      y: hit.craft.y + 1,
      z: hit.craft.z,
      nx: hit.nx,
      ny: hit.ny,
      nz: hit.nz,
    };
  }

  transformTextureDraft(
    axis: BlockTransformAxis,
    mirror: boolean,
    direction: 1 | -1,
  ): boolean {
    const hit = this.textureHit;
    if (!hit) return false;
    const draft = this.textureDrafts.get(craftKey(hit.craft));
    if (!draft) return false;
    draft.viewTransform = mirror
      ? mirrorBlockTransform(draft.viewTransform, axis)
      : rotateBlockTransform(draft.viewTransform, axis, direction);
    this.rebuildTextureDraft(hit.craft);
    return true;
  }

  requestFaceAction(action: CraftFaceAction, craft: CraftPosition): void {
    if (this.world.getBlock(craft.x, craft.y, craft.z) !== CRAFT_BLOCK_TYPE) {
      showToast('Craft block is no longer available');
      return;
    }
    this.openConfirmation(action, craft);
  }

  update(
    camera: THREE.PerspectiveCamera,
    active: boolean,
    dt: number,
    primaryHeld: boolean,
    control: boolean,
    massBuild: boolean,
    selectedStandardType: number | null,
    selectedLibraryBlock: LibraryBlockDefinition | null,
    selectedColor: string | null,
    worldHit: VoxelHit | null,
  ): void {
    this.cleanupRemovedCraftBlocks();
    if (!active) {
      this.clearTarget();
      return;
    }
    camera.getWorldDirection(this.direction);
    this.ray.set(camera.position, this.direction);
    this.massBuild = massBuild;
    this.selectedVoxelMaterial = selectedStandardType
      ?? (selectedLibraryBlock?.kind === 'solid'
        ? selectedColor ?? selectedLibraryBlock.color
        : null);
    this.findTarget(camera.position, worldHit);
    // Texture editing is only for existing textured library blocks created in inventory.
    this.textureModePreview = !!(
      this.activeCraft &&
      selectedLibraryBlock?.kind === 'textured' &&
      this.draftFor(this.activeCraft).size === 0 &&
      !this.textureDrafts.has(craftKey(this.activeCraft))
    );
    this.updateVisuals(control || this.removeTarget !== null);
    this.updateRemoval(dt, primaryHeld, control);
  }

  /** Returns true when the click belongs to the micro-voxel editor. */
  handleClick(
    button: number,
    standardType: number | null,
    customBlock: CustomBlockDefinition | null,
    libraryBlock: LibraryBlockDefinition | null,
    selectedColor: string | null,
    control: boolean,
    massBuild: boolean,
  ): boolean {
    if (this.textureHit) {
      const draft = this.textureDrafts.get(craftKey(this.textureHit.craft));
      if (!draft) return false;
      if (button === 2 || button === 1) {
        if (!selectedColor) {
          showToast('Select a solid color block to paint the texture');
          return true;
        }
        if (
          voxelMaterialGlow(selectedColor) > 0
          || (libraryBlock?.kind === 'solid' && (libraryBlock.glowIntensity ?? 0) > 0)
        ) {
          showToast('Glowing solids cannot paint textured blocks');
          return true;
        }
        // Textures store #rrggbbaa only (no glow byte).
        draft.pixels[this.textureHit.face][this.textureHit.index] =
          selectedColor.length >= 9 ? selectedColor.slice(0, 9) : selectedColor;
        this.rebuildTextureDraft(this.textureHit.craft);
        return true;
      }
      if (button === 0) {
        draft.pixels[this.textureHit.face][this.textureHit.index] = draft.color;
        this.rebuildTextureDraft(this.textureHit.craft);
        return true;
      }
    }
    if (button === 0) {
      if (!this.microHit) return false;
      if (control) this.removeVoxel(this.microHit);
      else {
        this.removeTarget = { ...this.microHit, craft: { ...this.microHit.craft } };
        this.removeElapsed = 0;
        this.progress.classList.add('visible');
      }
      return true;
    }
    if (button === 1) {
      if (!this.microHit) return false;
      this.cancelRemoval();
      const material: VoxelMaterial | null = standardType
        ?? (libraryBlock?.kind === 'solid'
          ? selectedColor ?? packVoxelMaterial(
            libraryBlock.color,
            libraryBlock.opacity,
            libraryBlock.glowIntensity,
          )
          : null);
      if (material === null) {
        showToast('Select a solid color block for custom-geometry voxels');
        return true;
      }
      const hit = this.microHit;
      const gridSize = this.gridSizeFor(hit.craft);
      const index = microVoxelIndex(hit.x, hit.y, hit.z, gridSize);
      const draft = this.draftFor(hit.craft);
      if (
        massBuild &&
        this.anchor &&
        this.anchorKind === 'replace' &&
        this.anchor.type === material &&
        craftKey(this.anchor.craft) === craftKey(hit.craft)
      ) {
        this.replaceHollowCuboid(this.anchor, hit, material);
        return true;
      }
      if (draft.get(index) !== material) {
        draft.set(index, material);
        this.rebuildDraft(hit.craft);
      }
      this.anchor = {
        craft: { ...hit.craft },
        x: hit.x,
        y: hit.y,
        z: hit.z,
        type: material,
      };
      this.anchorKind = 'replace';
      return true;
    }
    if (button !== 2) return false;
    const material: VoxelMaterial | null = standardType
      ?? (libraryBlock?.kind === 'solid'
        ? selectedColor ?? packVoxelMaterial(
          libraryBlock.color,
          libraryBlock.opacity,
          libraryBlock.glowIntensity,
        )
        : null);
    if (
      massBuild &&
      this.anchor &&
      this.anchorKind === 'replace' &&
      material !== null &&
      this.anchor.type === material &&
      this.microHit &&
      craftKey(this.anchor.craft) === craftKey(this.microHit.craft)
    ) {
      this.replaceHollowCuboid(this.anchor, this.microHit, material);
      return true;
    }
    if (!this.placementTarget) return false;
    const target = this.placementTarget;
    const craft = target.craft;
    const key = craftKey(craft);
    const draft = this.draftFor(craft);
    if (
      libraryBlock?.kind === 'textured' &&
      draft.size === 0 &&
      !this.textureDrafts.has(key)
    ) {
      this.textureDrafts.set(key, {
        color: packVoxelMaterial(libraryBlock.color, libraryBlock.opacity, null),
        pixels: libraryBlock.pixels.map((face) => [...face]),
        gridSize: libraryBlock.gridSize,
        source: libraryBlock,
        viewTransform: 0,
      });
      this.rebuildTextureDraft(craft);
      showToast(`Loaded “${libraryBlock.name}” texture for editing`);
      return true;
    }
    if (material === null) {
      if (customBlock) {
        if (draft.size > 0) {
          showToast('Craft block must be empty before loading a custom block');
          return true;
        }
        this.draftGridSizes.set(key, customBlock.gridSize);
        this.draftSources.set(key, customBlock);
        for (const [index, type] of customBlock.voxels) draft.set(index, type);
        this.clearAnchor();
        this.rebuildDraft(craft);
        showToast(`Loaded “${customBlock.name}” for editing`);
        return true;
      }
      if (libraryBlock?.kind === 'textured') {
        showToast('RMB an empty craft workspace to paint this texture');
      } else {
        showToast('Select a solid color block for custom-geometry voxels');
      }
      return true;
    }
    const gridSize = this.gridSizeFor(target.craft);
    if (!insideGrid(target.x, target.y, target.z, gridSize)) return true;
    if (
      massBuild &&
      this.anchor &&
      this.anchorKind === 'place' &&
      this.anchor.type === material &&
      craftKey(this.anchor.craft) === craftKey(target.craft)
    ) {
      this.buildHollowCuboid(this.anchor, target, material);
      return true;
    }
    draft.set(microVoxelIndex(target.x, target.y, target.z, gridSize), material);
    this.anchor = { ...target, craft: { ...target.craft }, type: material };
    this.anchorKind = 'place';
    this.rebuildDraft(target.craft);
    return true;
  }

  private findTarget(origin: THREE.Vector3, worldHit: VoxelHit | null): void {
    this.activeCraft = null;
    this.microHit = null;
    this.placementTarget = null;
    this.textureHit = null;
    let nearestDistance = Infinity;
    let blockingDistance = Infinity;
    if (worldHit) {
      this.box.min.set(worldHit.x, worldHit.y, worldHit.z);
      this.box.max.set(worldHit.x + 1, worldHit.y + 1, worldHit.z + 1);
      const solidPoint = this.ray.intersectBox(this.box, this.point);
      if (solidPoint) blockingDistance = origin.distanceTo(solidPoint);
    }

    for (const craft of this.world.getCraftBlocks()) {
      this.box.min.set(craft.x, craft.y + 1, craft.z);
      this.box.max.set(craft.x + 1, craft.y + 2, craft.z + 1);
      const intersection = this.ray.intersectBox(this.box, this.point);
      if (!intersection) continue;
      const distance = origin.distanceTo(intersection);
      if (
        distance > REACH ||
        distance > blockingDistance + 1e-5 ||
        distance >= nearestDistance
      ) continue;
      nearestDistance = distance;
      this.activeCraft = craft;
      const textureDraft = this.textureDrafts.get(craftKey(craft));
      if (textureDraft) {
        this.textureHit = textureHitAt(
          craft,
          intersection,
          textureDraft.viewTransform,
          textureDraft.gridSize,
        );
        continue;
      }
      const gridSize = this.gridSizeFor(craft);
      this.microHit = raycastMicroVoxels(
        origin,
        this.direction,
        craft,
        this.draftFor(craft),
        gridSize,
        REACH,
      );
    }

    if (this.textureHit) return;
    if (this.microHit) {
      this.placementTarget = {
        craft: this.microHit.craft,
        x: this.microHit.x + this.microHit.nx,
        y: this.microHit.y + this.microHit.ny,
        z: this.microHit.z + this.microHit.nz,
      };
      return;
    }

    if (
      worldHit &&
      worldHit.ny === 1 &&
      this.world.getBlock(worldHit.x, worldHit.y, worldHit.z) === CRAFT_BLOCK_TYPE
    ) {
      const craft = { x: worldHit.x, y: worldHit.y, z: worldHit.z };
      const gridSize = this.gridSizeFor(craft);
      const t = (craft.y + 1 - origin.y) / this.direction.y;
      if (t >= 0 && t <= REACH) {
        const px = origin.x + this.direction.x * t;
        const pz = origin.z + this.direction.z * t;
        this.activeCraft = craft;
        this.placementTarget = {
          craft,
          x: clampCell(Math.floor((px - craft.x) * gridSize), gridSize),
          y: 0,
          z: clampCell(Math.floor((pz - craft.z) * gridSize), gridSize),
        };
      }
    }
  }

  private updateVisuals(removeMode: boolean): void {
    const craft = this.activeCraft;
    // The full-cell boundary competes visually with world highlighting. The
    // nearest actionable target below is the only selection outline shown.
    this.boundary.visible = false;
    if (craft) {
      const key = craftKey(craft);
      const source = this.draftSources.get(key);
      const texture = this.textureDrafts.get(key);
      this.status.textContent = this.textureModePreview
        ? 'Texture mode preview · RMB: load textured block for painting'
        : texture
        ? `${texture.source ? `Editing “${texture.source.name}”` : 'Texture cube'} · ` +
          'RMB/MMB: paint · LMB: restore base color'
        : `${source ? `Editing “${source.name}”` : 'Custom geometry'} · ` +
          `${this.draftFor(craft).size} voxels · MMB: replace · Alt+RMB: mass add/replace`;
      this.status.classList.add('visible');
    } else {
      this.status.classList.remove('visible');
    }
    if (this.textureModePreview && craft) {
      this.boundary.position.set(craft.x + 0.5, craft.y + 1.5, craft.z + 0.5);
      this.boundary.visible = true;
      this.voxelHighlight.visible = false;
      this.massPreview.visible = false;
      return;
    }
    if (this.textureHit) {
      const hit = this.textureHit;
      const faceSize = this.textureDrafts.get(craftKey(hit.craft))?.gridSize
        ?? TEXTURE_GRID_SIZE;
      this.voxelHighlight.visible = true;
      this.voxelHighlightMaterial.color.setHex(0xffffff);
      this.voxelHighlight.scale.setScalar(0.9 / faceSize);
      this.voxelHighlight.position.set(hit.cx, hit.cy, hit.cz);
      orientFaceOutline(this.voxelHighlight, hit.nx, hit.ny, hit.nz);
      this.massPreview.visible = false;
      return;
    }
    const target = removeMode ? this.microHit : this.placementTarget;
    const gridSize = target ? this.gridSizeFor(target.craft) : CRAFT_GRID_SIZE;
    const valid = !!target && insideGrid(target.x, target.y, target.z, gridSize);
    this.voxelHighlight.visible = valid;
    if (valid && target) {
      this.voxelHighlightMaterial.color.setHex(removeMode ? 0xff5757 : 0xffffff);
      this.voxelHighlight.scale.setScalar(0.9 / gridSize);
      if (this.microHit) {
        const hit = this.microHit;
        const half = 0.5 / gridSize + 0.0005;
        this.voxelHighlight.position.set(
          hit.craft.x + (hit.x + 0.5) / gridSize + hit.nx * half,
          hit.craft.y + 1 + (hit.y + 0.5) / gridSize + hit.ny * half,
          hit.craft.z + (hit.z + 0.5) / gridSize + hit.nz * half,
        );
        orientFaceOutline(this.voxelHighlight, hit.nx, hit.ny, hit.nz);
      } else {
        // An empty editor is focused through the craft block's top face.
        this.voxelHighlight.position.set(
          target.craft.x + (target.x + 0.5) / gridSize,
          target.craft.y + 1 + 0.0005,
          target.craft.z + (target.z + 0.5) / gridSize,
        );
        orientFaceOutline(this.voxelHighlight, 0, 1, 0);
      }
    }
    this.updateMassPreview();
  }

  private updateMassPreview(): void {
    const anchor = this.anchor;
    const replace = this.anchorKind === 'replace';
    const target = replace ? this.microHit : this.placementTarget;
    const gridSize = target ? this.gridSizeFor(target.craft) : CRAFT_GRID_SIZE;
    if (
      !this.massBuild ||
      !target ||
      !anchor ||
      !this.anchorKind ||
      this.selectedVoxelMaterial === null ||
      anchor.type !== this.selectedVoxelMaterial ||
      craftKey(anchor.craft) !== craftKey(target.craft) ||
      !insideGrid(target.x, target.y, target.z, gridSize) ||
      (replace && !this.draftFor(target.craft).has(
        microVoxelIndex(target.x, target.y, target.z, gridSize),
      ))
    ) {
      this.massPreview.visible = false;
      return;
    }
    const minX = Math.min(anchor.x, target.x);
    const maxX = Math.max(anchor.x, target.x);
    const minY = Math.min(anchor.y, target.y);
    const maxY = Math.max(anchor.y, target.y);
    const minZ = Math.min(anchor.z, target.z);
    const maxZ = Math.max(anchor.z, target.z);
    this.massPreview.position.set(
      target.craft.x + (minX + maxX + 1) / (2 * gridSize),
      target.craft.y + 1 + (minY + maxY + 1) / (2 * gridSize),
      target.craft.z + (minZ + maxZ + 1) / (2 * gridSize),
    );
    this.massPreview.scale.set(
      (maxX - minX + 1) / gridSize,
      (maxY - minY + 1) / gridSize,
      (maxZ - minZ + 1) / gridSize,
    );
    this.massPreview.visible = true;
  }

  private buildHollowCuboid(
    anchor: MicroAnchor,
    target: PlacementTarget,
    type: VoxelMaterial,
  ): void {
    const minX = Math.min(anchor.x, target.x);
    const maxX = Math.max(anchor.x, target.x);
    const minY = Math.min(anchor.y, target.y);
    const maxY = Math.max(anchor.y, target.y);
    const minZ = Math.min(anchor.z, target.z);
    const maxZ = Math.max(anchor.z, target.z);
    const draft = this.draftFor(target.craft);
    const gridSize = this.gridSizeFor(target.craft);
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        for (let x = minX; x <= maxX; x++) {
          if (
            x === minX || x === maxX ||
            y === minY || y === maxY ||
            z === minZ || z === maxZ
          ) {
            draft.set(microVoxelIndex(x, y, z, gridSize), type);
          }
        }
      }
    }
    this.clearAnchor();
    this.massPreview.visible = false;
    this.rebuildDraft(target.craft);
  }

  /** Hollow shell replace: only changes voxels that already exist. */
  private replaceHollowCuboid(
    anchor: MicroAnchor,
    target: PlacementTarget,
    type: VoxelMaterial,
  ): void {
    const minX = Math.min(anchor.x, target.x);
    const maxX = Math.max(anchor.x, target.x);
    const minY = Math.min(anchor.y, target.y);
    const maxY = Math.max(anchor.y, target.y);
    const minZ = Math.min(anchor.z, target.z);
    const maxZ = Math.max(anchor.z, target.z);
    const draft = this.draftFor(target.craft);
    const gridSize = this.gridSizeFor(target.craft);
    for (let y = minY; y <= maxY; y++) {
      for (let z = minZ; z <= maxZ; z++) {
        for (let x = minX; x <= maxX; x++) {
          if (
            x === minX || x === maxX ||
            y === minY || y === maxY ||
            z === minZ || z === maxZ
          ) {
            const index = microVoxelIndex(x, y, z, gridSize);
            if (draft.has(index) && draft.get(index) !== type) {
              draft.set(index, type);
            }
          }
        }
      }
    }
    this.clearAnchor();
    this.massPreview.visible = false;
    this.rebuildDraft(target.craft);
  }

  private clearAnchor(): void {
    this.anchor = null;
    this.anchorKind = null;
  }

  private updateRemoval(dt: number, held: boolean, control: boolean): void {
    if (!this.removeTarget) return;
    if (!held || !sameMicroVoxel(this.removeTarget, this.microHit)) {
      this.cancelRemoval();
      return;
    }
    if (control) {
      this.removeVoxel(this.removeTarget);
      return;
    }
    this.removeElapsed = Math.min(BREAK_SECONDS, this.removeElapsed + dt);
    this.progressFill.style.width = `${Math.round(this.removeElapsed * 100)}%`;
    if (this.removeElapsed >= BREAK_SECONDS) this.removeVoxel(this.removeTarget);
  }

  private removeVoxel(hit: MicroHit): void {
    this.draftFor(hit.craft).delete(
      microVoxelIndex(hit.x, hit.y, hit.z, this.gridSizeFor(hit.craft)),
    );
    this.rebuildDraft(hit.craft);
    this.cancelRemoval();
  }

  private cancelRemoval(): void {
    this.removeTarget = null;
    this.removeElapsed = 0;
    this.progress.classList.remove('visible');
    this.progressFill.style.width = '0';
  }

  private clearTarget(): void {
    this.textureModePreview = false;
    this.activeCraft = null;
    this.microHit = null;
    this.placementTarget = null;
    this.textureHit = null;
    this.boundary.visible = false;
    this.voxelHighlight.visible = false;
    this.massPreview.visible = false;
    this.status.classList.remove('visible');
    this.cancelRemoval();
  }

  private draftFor(craft: CraftPosition): Map<number, VoxelMaterial> {
    const key = craftKey(craft);
    let draft = this.drafts.get(key);
    if (!draft) {
      draft = new Map();
      this.drafts.set(key, draft);
    }
    return draft;
  }

  private gridSizeFor(craft: CraftPosition): number {
    return this.draftGridSizes.get(craftKey(craft)) ?? CRAFT_GRID_SIZE;
  }

  private rebuildDraft(craft: CraftPosition): void {
    const key = craftKey(craft);
    const old = this.draftMeshes.get(key);
    if (old) {
      this.scene.remove(old);
      disposeDraftGroup(old);
      this.draftMeshes.delete(key);
    }
    const draft = this.draftFor(craft);
    if (draft.size === 0) return;
    const geometries = buildMicroVoxelGeometries(
      [...draft.entries()],
      this.gridSizeFor(craft),
    );
    const group = new THREE.Group();
    group.position.set(craft.x, craft.y + 1, craft.z);
    const castShadow = [...draft.values()].some((material) => isVoxelOpaque(material));
    if (!isEmptyGeometry(geometries.opaque)) {
      const opaque = new THREE.Mesh(geometries.opaque, this.opaqueMaterial);
      opaque.castShadow = castShadow;
      opaque.receiveShadow = true;
      group.add(opaque);
    } else {
      geometries.opaque.dispose();
    }
    if (!isEmptyGeometry(geometries.transparent)) {
      const transparent = new THREE.Mesh(
        geometries.transparent,
        this.transparentMaterial,
      );
      transparent.receiveShadow = true;
      transparent.renderOrder = 1;
      group.add(transparent);
    } else {
      geometries.transparent.dispose();
    }
    if (!isEmptyGeometry(geometries.glowing)) {
      const glowing = new THREE.Mesh(geometries.glowing, this.glowingMaterial);
      glowing.renderOrder = 2;
      group.add(glowing);
    } else {
      geometries.glowing.dispose();
    }
    this.draftMeshes.set(key, group);
    this.scene.add(group);
  }

  private rebuildTextureDraft(craft: CraftPosition): void {
    const key = craftKey(craft);
    const old = this.textureMeshes.get(key);
    if (old) {
      this.scene.remove(old);
      disposeTextureMesh(old);
      this.textureMeshes.delete(key);
    }
    const draft = this.textureDrafts.get(key);
    if (!draft) return;
    const geometry = transformedTextureCubeGeometry(draft.viewTransform);
    const mesh = new THREE.Mesh(geometry, textureMaterials(draft.pixels));
    mesh.position.set(craft.x + 0.5, craft.y + 1.5, craft.z + 0.5);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.textureMeshes.set(key, mesh);
    this.scene.add(mesh);
  }

  private cleanupRemovedCraftBlocks(): void {
    const live = new Set(this.world.getCraftBlocks().map(craftKey));
    for (const key of this.drafts.keys()) {
      if (live.has(key)) continue;
      this.drafts.delete(key);
      this.draftGridSizes.delete(key);
      this.draftSources.delete(key);
      if (this.anchor && craftKey(this.anchor.craft) === key) this.clearAnchor();
      const mesh = this.draftMeshes.get(key);
      if (mesh) {
        this.scene.remove(mesh);
        disposeDraftGroup(mesh);
        this.draftMeshes.delete(key);
      }
    }
    for (const key of this.textureDrafts.keys()) {
      if (live.has(key)) continue;
      this.textureDrafts.delete(key);
      const mesh = this.textureMeshes.get(key);
      if (mesh) {
        this.scene.remove(mesh);
        disposeTextureMesh(mesh);
        this.textureMeshes.delete(key);
      }
    }
  }

  private openConfirmation(action: CraftFaceAction, craft: CraftPosition): void {
    const key = craftKey(craft);
    const draft = this.draftFor(craft);
    const gridSize = this.gridSizeFor(craft);
    const source = this.draftSources.get(key) ?? null;
    const texture = this.textureDrafts.get(key) ?? null;
    const loadedSource = texture?.source ?? source;
    if (action !== 'reset' && draft.size === 0 && !texture) {
      showToast('Add at least one voxel before saving');
      return;
    }
    if (action === 'overwrite' && !loadedSource) {
      showToast('Load an existing block before overwriting');
      return;
    }
    if (action === 'reset' && draft.size === 0 && !texture) {
      showToast('Craft draft is already empty');
      return;
    }
    const title = action === 'save'
      ? 'Save as a new block?'
      : action === 'overwrite'
        ? 'Overwrite the loaded block?'
        : 'Reset this craft draft?';
    const detail = action === 'reset'
      ? texture
        ? 'This will remove the painted texture draft.'
        : `This will remove all ${draft.size} voxels from the draft.`
      : texture
        ? `Six ${texture.gridSize}×${texture.gridSize} pixel faces will be saved as a cube texture.`
        : `${draft.size} of ${gridSize ** 3} voxels will be saved.`;
    this.dialog.innerHTML = `
      <div class="craft-dialog-card">
        <h2>${title}</h2>
        <p>${detail}</p>
        ${action === 'reset'
          ? ''
          : '<input class="craft-name" maxlength="64" />'}
        <div class="craft-dialog-actions">
          <button type="button" class="confirm-craft">${
            action === 'save' ? 'Save new block' :
            action === 'overwrite' ? 'Overwrite block' : 'Reset draft'
          }</button>
          <button type="button" class="cancel-craft secondary">Cancel</button>
        </div>
      </div>`;
    this.dialog.classList.add('visible');
    this.dialog.onclick = (event) => event.stopPropagation();
    if (document.pointerLockElement) document.exitPointerLock();

    const close = (relock: boolean) => {
      this.dialog.classList.remove('visible');
      if (relock) this.options.canvas.requestPointerLock();
    };
    this.dialog.querySelector<HTMLButtonElement>('.cancel-craft')!.onclick =
      () => close(true);
    const nameInput = this.dialog.querySelector<HTMLInputElement>('.craft-name');
    if (nameInput) {
      nameInput.value = action === 'overwrite'
        ? loadedSource!.name
        : loadedSource
          ? `${loadedSource.name} copy`
          : texture ? 'Textured block' : 'Custom block';
      nameInput.select();
    }
    this.dialog.querySelector<HTMLButtonElement>('.confirm-craft')!.onclick =
      async () => {
        if (action === 'reset') {
          draft.clear();
          this.draftGridSizes.delete(key);
          this.draftSources.delete(key);
          this.textureDrafts.delete(key);
          this.clearAnchor();
          this.massPreview.visible = false;
          this.rebuildDraft(craft);
          this.rebuildTextureDraft(craft);
          showToast('Craft block reset');
          close(true);
          return;
        }
        const name = nameInput!.value.trim();
        if (!name) {
          showToast('Enter a block name');
          return;
        }
        const confirm = this.dialog.querySelector<HTMLButtonElement>('.confirm-craft')!;
        confirm.disabled = true;
        try {
          if (texture) {
            const definition = await this.options.saveTexture(
              name,
              texture.color,
              texture.pixels,
              action === 'overwrite' ? texture.source!.id : null,
            );
            if (definition.kind !== 'textured') throw new Error('Invalid textured block response');
            texture.source = definition;
            texture.gridSize = definition.gridSize;
            showToast(action === 'overwrite'
              ? `Updated “${name}”`
              : `Saved “${name}” as a new textured block`);
            close(true);
            return;
          }
          const definition = await this.options.save(
            name,
            gridSize,
            [...draft.entries()].sort((a, b) => a[0] - b[0]),
            action === 'overwrite' ? source!.id : null,
          );
          this.draftSources.set(key, definition);
          this.draftGridSizes.set(key, definition.gridSize);
          showToast(action === 'overwrite'
            ? `Updated “${name}”`
            : `Saved “${name}” as a new block`);
          close(true);
        } catch (error) {
          showToast(error instanceof Error ? error.message : 'Could not save custom block');
          confirm.disabled = false;
        }
      };
  }
}

function textureHitAt(
  craft: CraftPosition,
  point: THREE.Vector3,
  transform: number,
  gridSize: number,
): TextureHit {
  const orientation = textureTransformMatrix(transform);
  const canonical = new THREE.Vector3(
    point.x - craft.x - 0.5,
    point.y - craft.y - 1.5,
    point.z - craft.z - 0.5,
  ).applyMatrix4(orientation.clone().invert()).addScalar(0.5);
  const x = THREE.MathUtils.clamp(canonical.x, 0, 1);
  const y = THREE.MathUtils.clamp(canonical.y, 0, 1);
  const z = THREE.MathUtils.clamp(canonical.z, 0, 1);
  let face = 0;
  let nx = 0;
  let ny = 0;
  let nz = 0;
  let u = 0;
  let v = 0;
  if (x < 1e-5) {
    face = 1; nx = -1; u = z; v = y;
  } else if (x > 1 - 1e-5) {
    face = 0; nx = 1; u = 1 - z; v = y;
  } else if (y < 1e-5) {
    face = 3; ny = -1; u = x; v = z;
  } else if (y > 1 - 1e-5) {
    face = 2; ny = 1; u = x; v = 1 - z;
  } else if (z < 1e-5) {
    face = 5; nz = -1; u = 1 - x; v = y;
  } else {
    face = 4; nz = 1; u = x; v = y;
  }
  const column = clampCell(Math.floor(u * gridSize), gridSize);
  const row = clampCell(Math.floor(v * gridSize), gridSize);
  const centerU = (column + 0.5) / gridSize;
  const centerV = (row + 0.5) / gridSize;
  let cx = craft.x + x;
  let cy = craft.y + 1 + y;
  let cz = craft.z + z;
  const offset = 0.0005;
  if (face === 0) {
    cx = craft.x + 1 + offset; cy = craft.y + 1 + centerV; cz = craft.z + 1 - centerU;
  } else if (face === 1) {
    cx = craft.x - offset; cy = craft.y + 1 + centerV; cz = craft.z + centerU;
  } else if (face === 2) {
    cx = craft.x + centerU; cy = craft.y + 2 + offset; cz = craft.z + 1 - centerV;
  } else if (face === 3) {
    cx = craft.x + centerU; cy = craft.y + 1 - offset; cz = craft.z + centerV;
  } else if (face === 4) {
    cx = craft.x + centerU; cy = craft.y + 1 + centerV; cz = craft.z + 1 + offset;
  } else {
    cx = craft.x + 1 - centerU; cy = craft.y + 1 + centerV; cz = craft.z - offset;
  }
  const transformedCenter = new THREE.Vector3(
    cx - craft.x - 0.5,
    cy - craft.y - 1.5,
    cz - craft.z - 0.5,
  ).applyMatrix4(orientation);
  cx = craft.x + 0.5 + transformedCenter.x;
  cy = craft.y + 1.5 + transformedCenter.y;
  cz = craft.z + 0.5 + transformedCenter.z;
  const transformedNormal = new THREE.Vector3(nx, ny, nz).transformDirection(orientation);
  nx = Math.round(transformedNormal.x);
  ny = Math.round(transformedNormal.y);
  nz = Math.round(transformedNormal.z);
  return {
    craft,
    face,
    index: row * gridSize + column,
    nx,
    ny,
    nz,
    u,
    v,
    cx,
    cy,
    cz,
  };
}

function textureMaterials(pixels: TexturePixels): THREE.MeshLambertMaterial[] {
  const size = textureGridSizeFromPixels(pixels) ?? TEXTURE_GRID_SIZE;
  return pixels.map((face) => {
    const data = new Uint8Array(size * size * 4);
    face.forEach((color, index) => {
      const [r, g, b, a] = rgbaBytes(color);
      data[index * 4] = r;
      data[index * 4 + 1] = g;
      data[index * 4 + 2] = b;
      data[index * 4 + 3] = a;
    });
    const texture = new THREE.DataTexture(
      data,
      size,
      size,
      THREE.RGBAFormat,
    );
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    const transparent = face.some((color) => rgbaBytes(color)[3] < 255);
    return new THREE.MeshLambertMaterial({
      map: texture,
      transparent,
      depthWrite: !transparent,
    });
  });
}


function rgbaBytes(color: string): [number, number, number, number] {
  const hex = color.slice(1);
  const value = Number.parseInt(hex, 16);
  return hex.length === 8
    ? [(value >>> 24) & 255, (value >>> 16) & 255, (value >>> 8) & 255, value & 255]
    : [(value >> 16) & 255, (value >> 8) & 255, value & 255, 255];
}

function transformedTextureCubeGeometry(transform: number): THREE.BoxGeometry {
  const matrix = textureTransformMatrix(transform);
  const geometry = new THREE.BoxGeometry(1, 1, 1).applyMatrix4(matrix);
  if (matrix.determinant() < 0) reverseTextureTriangleWinding(geometry);
  return geometry;
}

function textureTransformMatrix(transform: number): THREE.Matrix4 {
  const values = blockTransformMatrix(transform);
  return new THREE.Matrix4().set(
    values[0], values[1], values[2], 0,
    values[3], values[4], values[5], 0,
    values[6], values[7], values[8], 0,
    0, 0, 0, 1,
  );
}

function reverseTextureTriangleWinding(geometry: THREE.BufferGeometry): void {
  const index = geometry.getIndex();
  if (!index) return;
  for (let offset = 0; offset < index.count; offset += 3) {
    const second = index.getX(offset + 1);
    index.setX(offset + 1, index.getX(offset + 2));
    index.setX(offset + 2, second);
  }
  index.needsUpdate = true;
}

function disposeDraftGroup(group: THREE.Group): void {
  for (const child of group.children) {
    const mesh = child as THREE.Mesh;
    mesh.geometry.dispose();
  }
  group.clear();
}

function disposeTextureMesh(mesh: THREE.Mesh): void {
  mesh.geometry.dispose();
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  for (const material of materials) {
    const mapped = material as THREE.MeshLambertMaterial;
    mapped.map?.dispose();
    material.dispose();
  }
}

function raycastMicroVoxels(
  origin: THREE.Vector3,
  direction: THREE.Vector3,
  craft: CraftPosition,
  cells: Map<number, VoxelMaterial>,
  gridSize: number,
  maxDistance: number,
): MicroHit | null {
  if (cells.size === 0) return null;
  const box = new THREE.Box3(
    new THREE.Vector3(craft.x, craft.y + 1, craft.z),
    new THREE.Vector3(craft.x + 1, craft.y + 2, craft.z + 1),
  );
  const ray = new THREE.Ray(origin, direction);
  const entry = ray.intersectBox(box, new THREE.Vector3());
  if (!entry) return null;
  let travelled = origin.distanceTo(entry);
  if (travelled > maxDistance) return null;
  const start = entry.clone().addScaledVector(direction, 1e-6);
  let x = clampCell(Math.floor((start.x - craft.x) * gridSize), gridSize);
  let y = clampCell(Math.floor((start.y - craft.y - 1) * gridSize), gridSize);
  let z = clampCell(Math.floor((start.z - craft.z) * gridSize), gridSize);
  const stepX = Math.sign(direction.x);
  const stepY = Math.sign(direction.y);
  const stepZ = Math.sign(direction.z);
  const deltaX = stepX === 0 ? Infinity : 1 / (Math.abs(direction.x) * gridSize);
  const deltaY = stepY === 0 ? Infinity : 1 / (Math.abs(direction.y) * gridSize);
  const deltaZ = stepZ === 0 ? Infinity : 1 / (Math.abs(direction.z) * gridSize);
  const localX = (start.x - craft.x) * gridSize;
  const localY = (start.y - craft.y - 1) * gridSize;
  const localZ = (start.z - craft.z) * gridSize;
  let maxX = stepX === 0 ? Infinity : (stepX > 0 ? x + 1 - localX : localX - x) * deltaX;
  let maxY = stepY === 0 ? Infinity : (stepY > 0 ? y + 1 - localY : localY - y) * deltaY;
  let maxZ = stepZ === 0 ? Infinity : (stepZ > 0 ? z + 1 - localZ : localZ - z) * deltaZ;
  let nx = Math.abs(entry.x - craft.x) < 1e-5
    ? -1
    : Math.abs(entry.x - craft.x - 1) < 1e-5 ? 1 : 0;
  let ny = nx === 0 && Math.abs(entry.y - craft.y - 1) < 1e-5
    ? -1
    : nx === 0 && Math.abs(entry.y - craft.y - 2) < 1e-5 ? 1 : 0;
  let nz = nx === 0 && ny === 0 && Math.abs(entry.z - craft.z) < 1e-5
    ? -1
    : nx === 0 && ny === 0 && Math.abs(entry.z - craft.z - 1) < 1e-5 ? 1 : 0;

  while (insideGrid(x, y, z, gridSize) && travelled <= maxDistance) {
    if (cells.has(microVoxelIndex(x, y, z, gridSize))) {
      return { craft, x, y, z, nx, ny, nz };
    }
    if (maxX <= maxY && maxX <= maxZ) {
      travelled += maxX;
      maxY -= maxX;
      maxZ -= maxX;
      maxX = deltaX;
      x += stepX;
      nx = -stepX; ny = 0; nz = 0;
    } else if (maxY <= maxZ) {
      travelled += maxY;
      maxX -= maxY;
      maxZ -= maxY;
      maxY = deltaY;
      y += stepY;
      nx = 0; ny = -stepY; nz = 0;
    } else {
      travelled += maxZ;
      maxX -= maxZ;
      maxY -= maxZ;
      maxZ = deltaZ;
      z += stepZ;
      nx = 0; ny = 0; nz = -stepZ;
    }
  }
  return null;
}

function craftKey(craft: CraftPosition): string {
  return `${craft.x},${craft.y},${craft.z}`;
}

function insideGrid(x: number, y: number, z: number, gridSize: number): boolean {
  return x >= 0 && x < gridSize &&
    y >= 0 && y < gridSize &&
    z >= 0 && z < gridSize;
}

function clampCell(value: number, gridSize: number): number {
  return Math.max(0, Math.min(gridSize - 1, value));
}

function sameMicroVoxel(a: MicroHit, b: MicroHit | null): boolean {
  return !!b &&
    craftKey(a.craft) === craftKey(b.craft) &&
    a.x === b.x && a.y === b.y && a.z === b.z;
}
